import {SlotModuleV2} from '@gongxigames/framework/slotV2/baseClasses/slotModuleV2/SlotModuleV2';

import {
    IEventManagerWithAsync,
    ModuleInitPhase
} from '@gongxigames/framework/core';

import {LAYERS, SlotSettings} from '@gongxigames/framework/slot';
import {inject} from '@gongxigames/framework/core/installer';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {LoadingScreen} from '@gongxigames/framework/slotV2/modules/loadingScreenV2/components/LoadingScreen';
import {renderBackground, renderBanner, renderFullScreenButton, renderLogo, renderProgress, renderSlides, renderStartButton} from '@gongxigames/framework/slotV2/modules/loadingScreenV2';
import {$ArrowButton, $Arrows, $DotButton, $DotButtons, $Slides, $SlidesPresentation, $TextSlides} from '@gongxigames/framework/slotV2/modules/loadingScreenV2/components/slides';
import {Renderers, SlidesComponents} from '@gongxigames/framework/slotV2/modules/loadingScreenV2/types';
import {GameOnlyEventMap} from '@gongxigames/framework/slotV2/globals';

export class LoadingScreenModuleV2<T extends GameOnlyEventMap> extends SlotModuleV2<T> {
    public static readonly NAME = 'LoadingScreenModuleV2';
    public static layer = LAYERS.LOADER;
    public static readonly initPhase: ModuleInitPhase = 'initial';

    @inject(CoreDI.Settings)
    private settings!: SlotSettings;

    @inject(CoreDI.IEventManagerWithAsync)
    private eventManager!: IEventManagerWithAsync;

    protected loadingScreen!: LoadingScreen;
    private gameInitialized = false;
    private playIntroResolve?: () => void;

    protected defaultRenderers: Renderers = {
        renderBackground,
        renderBanner,
        renderFullScreenButton,
        renderLogo,
        renderProgress,
        renderSlides,
        renderStartButton,
    };

    protected defaultSlidesComponents: SlidesComponents = {
        $ArrowButton,
        $Arrows,
        $DotButton,
        $DotButtons,
        $Slides,
        $SlidesPresentation,
        $TextSlides,
    };

    protected override onInit(): void {
        this.loadingScreen = this.createLoadingScreen();

        this.loadingScreen.onStartButtonClicked = this.onStartButtonClicked.bind(this);
        this.container.addChild(this.loadingScreen.container);
    }

    /*
    Override createLoadingScreen to change your components:
    */
    protected createLoadingScreen(): LoadingScreen {
        return new LoadingScreen(
            this.defaultRenderers,
            this.defaultSlidesComponents
        );
    }

    protected override onSetupEvents(): void {
        this.emitter.on('General.Game.PLAY_INTRO', this.playIntro.bind(this));
        this.eventManager.on('General.Game.INITIALIZED', this.onGameInitialized.bind(this), this);
        this.eventManager.on('General.ResourcesLoading.PROGRESS', this.update.bind(this), this);
        this.gameInitialized = false;
        this.emitter.on('General.Game.PLAY_INTRO', this.onSetupEvents.bind(this));
    }

    private unsubscribeAll(): void {
        this.emitter.offByOwner('General.Game.PLAY_INTRO');
        this.eventManager.off('General.Game.INITIALIZED', this);
        this.eventManager.off('General.ResourcesLoading.PROGRESS', this);
    }

    private playIntro(): Promise<void> {
        return new Promise<void>(resolve => this.playIntroResolve = resolve);
    }

    private update(progress: number): void {
        this.loadingScreen.updateProgress(progress);
    }

    private onGameInitialized(): void {
        this.loadingScreen.completeLoading();
        this.gameInitialized = true;

        if (this.settings.get('showOddsUpfront')) {
            this.playIntroResolve?.();
        }
    }

    private removeBackground(): void {
        const rootContainer = this.store.stage_rootContainer.value;
        const loadingScreenSprite = rootContainer.getChildByName('loading_cover_sprite');
        const loadingScreenFader = rootContainer.getChildByName('loading_cover_fader');

        rootContainer.removeChild(loadingScreenSprite);
        rootContainer.removeChild(loadingScreenFader);
    }

    protected onStartButtonClicked(): void {

        if (!this.gameInitialized) {
            return;
        }

        this.removeBackground();
        this.unsubscribeAll();
        this.container.removeChild(this.loadingScreen.container);
        this.loadingScreen.container.destroy({children: false, texture: true, baseTexture: true});
        this.playIntroResolve?.();
    }
}